import { AxiosError } from "axios";

import { SessionContextProps } from "../Context/SessionContext";
import { EnhancedCenter } from "../Models/Center";
import { Customer, EnhancedCustomer, UnverifiedCustomer } from "../Models/Customer";
import { CustomerNote, EnhancedCustomerNote } from "../Models/CustomerNote";
import { DisqualifyingReason } from "../Models/DisqualifyingReason";
import { GemError } from "../Models/GemError";
import { Lead, LeadResult } from "../Models/Lead";
import INoteTag from "../V2/CustomerNotes/Types/INoteTag";
import { IMedicalFormConclusions, NullMedicalFormData } from "../V2/Playbook/Medical/Types/IMedicalFormConclusions";
import { PhoneNumberCache } from "./PhoneNumberCache";
import { Step, StepId, Steps } from "./Step";

/** A truncated version of `Session` which can be stored in the browser cache and restored. */
export class SessionCache {
  apptErrorCenters: string[];
  appointment?: { id: string; businessUnit: string; serviceSubCategory: string };
  customer: Customer;
  duplicateCustomers: Customer[];
  isCustomerValidForOpsBooking: boolean;
  isSessionShortCircuited: boolean;
  isSearchComplete: boolean;
  leadDisqualifiers: DisqualifyingReason[];
  leadResult: LeadResult | null;
  medicalConclusions: IMedicalFormConclusions;
  note: CustomerNote;
  noteTags: INoteTag[];
  phoneNumberCache: PhoneNumberCache;
  requestErrors: AxiosError<GemError>[];
  selectedCustomer?: EnhancedCustomer;
  stepId: StepId;
  unverifiedCustomer: UnverifiedCustomer;

  constructor(session: Session) {
    this.apptErrorCenters = session.apptErrorCenters;
    this.customer = EnhancedCustomer.toBase(session.customer);
    this.duplicateCustomers = session.duplicateCustomers;
    this.isCustomerValidForOpsBooking = session.isCustomerValidForOpsBooking;
    this.isSessionShortCircuited = session.isSessionShortCircuited;
    this.isSearchComplete = session.isSearchComplete;
    this.leadDisqualifiers = session.leadDisqualifiers;
    this.leadResult = session.leadResult;
    this.medicalConclusions = session.medicalConclusions;
    this.note = EnhancedCustomerNote.toBase(session.note);
    this.noteTags = session.note.tags;
    this.phoneNumberCache = session.phoneNumberCache;
    this.requestErrors = session.requestErrors;
    this.selectedCustomer = session.selectedCustomer && new EnhancedCustomer(session.selectedCustomer);
    this.stepId = session.step.id;
    this.unverifiedCustomer = new UnverifiedCustomer(session.customer);
  }
}

/** A object containing all of the data necessary to restore a Session Context. */
export class Session
  implements
    Pick<
      SessionContextProps,
      | "apptErrorCenters"
      | "appointment"
      | "customer"
      | "duplicateCustomers"
      | "isCustomerValidForOpsBooking"
      | "isSessionShortCircuited"
      | "isSearchComplete"
      | "leadDisqualifiers"
      | "leadResult"
      | "medicalConclusions"
      | "note"
      | "phoneNumberCache"
      | "requestErrors"
      | "selectedCustomer"
      | "step"
      | "unverifiedCustomer"
    >
{
  apptErrorCenters: string[];
  appointment?: { id: string; businessUnit: string; serviceSubCategory: string };
  customer: EnhancedCustomer;
  duplicateCustomers: Customer[];
  isCustomerValidForOpsBooking: boolean;
  isSessionShortCircuited: boolean;
  isSearchComplete: boolean;
  leadDisqualifiers: DisqualifyingReason[];
  leadResult: LeadResult | null;
  medicalConclusions: IMedicalFormConclusions;
  note: EnhancedCustomerNote;
  phoneNumberCache: PhoneNumberCache;
  requestErrors: AxiosError<GemError>[];
  selectedCustomer?: EnhancedCustomer;
  step: Step;
  unverifiedCustomer: UnverifiedCustomer;

  constructor(
    lead: Lead,
    centers: EnhancedCenter[],
    isManualLead: boolean,
    disqualReasons: DisqualifyingReason[],
    sessionCache?: SessionCache
  ) {
    this.medicalConclusions = sessionCache?.medicalConclusions ?? new NullMedicalFormData();
    this.apptErrorCenters = sessionCache?.apptErrorCenters || [];
    if (sessionCache?.customer) this.customer = new EnhancedCustomer(sessionCache.customer);
    else if (lead.outbound?.customer)
      this.customer = new EnhancedCustomer({
        ...lead.outbound.customer,
        centerId: lead.centerId,
        center: lead.centerId ? centers.find(({ id }) => id === lead.centerId) : undefined
      });
    else {
      const { firstName, lastName, phoneNumber, email, centerId, zipCode } = lead;
      this.customer = new EnhancedCustomer({
        firstName,
        lastName,
        phoneNumber,
        email,
        centerId,
        center: centerId ? centers.find(({ id }) => id === centerId) : undefined,
        zipCode
      });
    }
    this.duplicateCustomers = sessionCache?.duplicateCustomers || [];
    this.isCustomerValidForOpsBooking = sessionCache?.isCustomerValidForOpsBooking ?? false;
    this.isSessionShortCircuited = sessionCache?.isSessionShortCircuited ?? true;
    this.isSearchComplete = sessionCache?.isSearchComplete ?? false;
    this.leadDisqualifiers =
      sessionCache?.leadDisqualifiers ||
      Object.keys(lead.result?.disqualifyingReasons || {}).map(k => {
        const reason = disqualReasons.find(d => d.id === k);
        if (!reason) throw "Failed to lookup a disqualifying reason";
        return reason;
      });

    this.leadResult = sessionCache?.leadResult || lead.result;
    this.note = new EnhancedCustomerNote(sessionCache?.note, sessionCache?.noteTags);
    this.phoneNumberCache = sessionCache?.phoneNumberCache || {};
    this.requestErrors = sessionCache?.requestErrors || [];
    this.selectedCustomer = sessionCache?.selectedCustomer && new EnhancedCustomer(sessionCache.selectedCustomer);
    this.step = sessionCache
      ? // restore the cached step
        Steps[sessionCache.stepId]
      : // else if new session
      lead.outbound
      ? // and lead is outbound, set as Intro
        Steps[StepId.Intro]
      : isManualLead
      ? // else if lead is manual, set as Manual
        Steps[StepId.Manual]
      : // default search
        Steps[StepId.Search];
    this.unverifiedCustomer = sessionCache?.unverifiedCustomer ?? new UnverifiedCustomer(this.customer);
  }
}

export interface SaveCustomer {
  customer: EnhancedCustomer;
  override: boolean;
  forceUpdate?: true;
}
